/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { INITIALIZER_PROGRAM_ADDRESS } from '../programs';

export const SET_MIGRATOR_ALLOWLIST_DISCRIMINATOR = new Uint8Array([
  209, 90, 181, 104, 99, 108, 233, 168,
]);

export function getSetMigratorAllowlistDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SET_MIGRATOR_ALLOWLIST_DISCRIMINATOR,
  );
}

export type SetMigratorAllowlistInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TAccountConfig extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAdmin extends string
        ? ReadonlySignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      ...TRemainingAccounts,
    ]
  >;

export type SetMigratorAllowlistInstructionData = {
  discriminator: ReadonlyUint8Array;
  allowlist: Array<Address>;
};

export type SetMigratorAllowlistInstructionDataArgs = {
  allowlist: Array<Address>;
};

export function getSetMigratorAllowlistInstructionDataEncoder(): Encoder<SetMigratorAllowlistInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['allowlist', getArrayEncoder(getAddressEncoder())],
    ]),
    (value) => ({
      ...value,
      discriminator: SET_MIGRATOR_ALLOWLIST_DISCRIMINATOR,
    }),
  );
}

export function getSetMigratorAllowlistInstructionDataDecoder(): Decoder<SetMigratorAllowlistInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['allowlist', getArrayDecoder(getAddressDecoder())],
  ]);
}

export function getSetMigratorAllowlistInstructionDataCodec(): Codec<
  SetMigratorAllowlistInstructionDataArgs,
  SetMigratorAllowlistInstructionData
> {
  return combineCodec(
    getSetMigratorAllowlistInstructionDataEncoder(),
    getSetMigratorAllowlistInstructionDataDecoder(),
  );
}

export type SetMigratorAllowlistAsyncInput<
  TAccountAdmin extends string = string,
  TAccountConfig extends string = string,
> = {
  admin: TransactionSigner<TAccountAdmin>;
  config?: Address<TAccountConfig>;
  allowlist: SetMigratorAllowlistInstructionDataArgs['allowlist'];
};

export async function getSetMigratorAllowlistInstructionAsync<
  TAccountAdmin extends string,
  TAccountConfig extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: SetMigratorAllowlistAsyncInput<TAccountAdmin, TAccountConfig>,
  config?: { programAddress?: TProgramAddress },
): Promise<
  SetMigratorAllowlistInstruction<
    TProgramAddress,
    TAccountAdmin,
    TAccountConfig
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    admin: { value: input.admin ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([99, 111, 110, 102, 105, 103])),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('config', accounts.config),
    ],
    data: getSetMigratorAllowlistInstructionDataEncoder().encode(
      args as SetMigratorAllowlistInstructionDataArgs,
    ),
    programAddress,
  } as SetMigratorAllowlistInstruction<
    TProgramAddress,
    TAccountAdmin,
    TAccountConfig
  >);
}

export type SetMigratorAllowlistInput<
  TAccountAdmin extends string = string,
  TAccountConfig extends string = string,
> = {
  admin: TransactionSigner<TAccountAdmin>;
  config: Address<TAccountConfig>;
  allowlist: SetMigratorAllowlistInstructionDataArgs['allowlist'];
};

export function getSetMigratorAllowlistInstruction<
  TAccountAdmin extends string,
  TAccountConfig extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: SetMigratorAllowlistInput<TAccountAdmin, TAccountConfig>,
  config?: { programAddress?: TProgramAddress },
): SetMigratorAllowlistInstruction<
  TProgramAddress,
  TAccountAdmin,
  TAccountConfig
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    admin: { value: input.admin ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('config', accounts.config),
    ],
    data: getSetMigratorAllowlistInstructionDataEncoder().encode(
      args as SetMigratorAllowlistInstructionDataArgs,
    ),
    programAddress,
  } as SetMigratorAllowlistInstruction<
    TProgramAddress,
    TAccountAdmin,
    TAccountConfig
  >);
}

export type ParsedSetMigratorAllowlistInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    admin: TAccountMetas[0];
    config: TAccountMetas[1];
  };
  data: SetMigratorAllowlistInstructionData;
};

export function parseSetMigratorAllowlistInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSetMigratorAllowlistInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { admin: getNextAccount(), config: getNextAccount() },
    data: getSetMigratorAllowlistInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
